import { useEffect } from 'react'
import { useLocation } from 'wouter'
import { useLingui } from '@lingui/react/macro'

import { showToast, ToastType } from '@/features/toast'
import useCheckPermission from '../hooks/useCheckPermission'
import { Permission } from '../types'

type Props = React.PropsWithChildren<{
  permissions: Permission[]
  redirect?: string
  onNoPermission?: () => void
}>

export default function CheckPermission({
  children,
  permissions,
  redirect,
  onNoPermission
}: Props) {
  const { t } = useLingui()
  const isEnabled = useCheckPermission(permissions)
  const [, navigate] = useLocation()

  useEffect(() => {
    if (isEnabled || !redirect) return

    showToast({
      id: 'no-permission-redirect',
      type: ToastType.ERROR,
      message: t({
        comment:
          'Toaster error message when a user tries to access a resource that they have no permission to and the user was redirected',
        message:
          'You have no access to the request resource and were redirected'
      })
    })

    navigate(redirect)
  }, [isEnabled, redirect, navigate, t])

  useEffect(() => {
    if (isEnabled || !onNoPermission) return

    showToast({
      id: 'no-permission-callback',
      type: ToastType.ERROR,
      message: t({
        comment:
          'Generic Toaster error message when a user tries to access a resource and we instead do something else',
        message: 'You have no access'
      })
    })

    onNoPermission()
  }, [isEnabled, redirect, onNoPermission, t])

  return isEnabled ? children : null
}
